/**
 * usePageMeta — the title, description, canonical URL and robots directive of
 * the current page.
 *
 * Search engines render the app, so these client-side tags are what they
 * index. A page calls the hook once, before any early return, with what it
 * shows. On unmount the site defaults come back, so a page without the hook
 * never keeps the previous page's title. `noindex` keeps a page out of search
 * results (not found, failed loads, login and token pages).
 *
 * The canonical URL is the path without its query string: filters and tabs
 * (?tab=, ?j=, ?days=) are views of one page, not pages of their own.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_NAME = 'Food Safety Digest';

// Keep in step with the description in index.html.
const DEFAULT_DESCRIPTION =
  'RASFF notifications, food recalls, EU legislation in the pipeline and pesticide MRLs, ' +
  'from official sources, in one place.';

const MAX_DESCRIPTION = 160;

export interface PageMeta {
  /** Page title without the site name; null or empty for the site name alone. */
  title?: string | null;
  description?: string | null;
  noindex?: boolean;
}

function clip(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= MAX_DESCRIPTION) return flat;
  const cut = flat.slice(0, MAX_DESCRIPTION - 1);
  const space = cut.lastIndexOf(' ');
  return `${space > 80 ? cut.slice(0, space) : cut}…`;
}

function setMeta(name: string, content: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

export function usePageMeta({ title, description, noindex = false }: PageMeta): void {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    setMeta('description', description ? clip(description) : DEFAULT_DESCRIPTION);
    setMeta('robots', noindex ? 'noindex' : null);
    setCanonical(noindex ? null : `${window.location.origin}${pathname}`);
    return () => {
      document.title = SITE_NAME;
      setMeta('description', DEFAULT_DESCRIPTION);
      setMeta('robots', null);
      setCanonical(null);
    };
  }, [title, description, noindex, pathname]);
}
